/**
 * Location Background Image Utilities
 *
 * Centralized logic for handling location background images across the application.
 * Background images are stored in S3 at: {BASE_URL}/location-backgrounds/{location}-{size}.webp
 * where size is 'sm' (640px), 'md' (1024px), or 'lg' (1920px).
 */

import { BASE_URL } from '../../utils/constants/url-constants';

/**
 * Formats location name to kebab-case for background image URLs
 * This MUST match the logic in web-app/lambda/utils/s3/backgroundImageStorage.ts
 * 
 * Example: "Helsinki, Finland" -> "helsinki-finland"
 * Example: "Finland (generic)" -> "finland-generic"
 */
export function formatLocationToKebabCase(location: string): string {
  if (!location || typeof location !== 'string') {
    return 'location';
  }
  return location
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Gets the srcset string for responsive location background images (WebP format, multiple sizes).
 * 
 * Background images are stored in S3 at: {BASE_URL}/location-backgrounds/{location}-{size}.webp
 * where size is 'sm' (640px), 'md' (1024px), or 'lg' (1920px).
 * Uses absolute URLs so images work in both development and production.
 * 
 * @param location - The location name (e.g., "Helsinki, Finland" or "Finland (generic)")
 * @returns srcset string for the background image
 * 
 * @example
 * getLocationBackgroundImageSrcset('Helsinki, Finland') 
 * // Returns: 'https://www.jobcrawls.com/location-backgrounds/helsinki-finland-sm.webp 640w, https://www.jobcrawls.com/location-backgrounds/helsinki-finland-md.webp 1024w, https://www.jobcrawls.com/location-backgrounds/helsinki-finland-lg.webp 1920w'
 */
export function getLocationBackgroundImageSrcset(location: string): string {
  if (!location || typeof location !== 'string') {
    return '';
  }
  
  const kebab = formatLocationToKebabCase(location);
  return `${BASE_URL}/location-backgrounds/${kebab}-sm.webp 640w, ${BASE_URL}/location-backgrounds/${kebab}-md.webp 1024w, ${BASE_URL}/location-backgrounds/${kebab}-lg.webp 1920w`;
}

/**
 * Gets the default src (fallback) for a location background image.
 * Uses the 'md' size (1024px) as the default since it's a good middle ground.
 * Uses absolute URLs so images work in both development and production.
 * 
 * @param location - The location name (e.g., "Helsinki, Finland" or "Finland (generic)")
 * @returns URL to the 1024px WebP background image
 */
export function getLocationBackgroundImageDefaultSrc(location: string): string {
  if (!location || typeof location !== 'string') {
    return '';
  }
  
  const kebab = formatLocationToKebabCase(location);
  return `${BASE_URL}/location-backgrounds/${kebab}-md.webp`;
}

/**
 * Gets the URL for a specific size of a location background image.
 * 
 * @param location - The location name (e.g., "Helsinki, Finland" or "Finland (generic)")
 * @param size - The image size ('sm', 'md', or 'lg')
 * @returns URL to the specified size WebP background image
 */
export function getLocationBackgroundImageUrl(location: string, size: 'sm' | 'md' | 'lg' = 'md'): string {
  if (!location || typeof location !== 'string') {
    return '';
  }

  const kebab = formatLocationToKebabCase(location);
  return `${BASE_URL}/location-backgrounds/${kebab}-${size}.webp`;
}
